import { Immutable, castImmutable, produce } from "immer";
import { PrimitiveAtom, atom, useStore } from "jotai";
import { Dispatch } from "react";
import invariant from "tiny-invariant";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { CmsBlock, CmsLayout, ReactableId, zCmsBlock } from "@/gen/realtime";

import { OnPatches } from "./undoHandler";

export const zTargetData = z.discriminatedUnion("t", [
    z.object({
        t: z.literal("move"),
        depth: z.number(),
        index: z.number(),
        parentId: z.string().nullable(),
    }),
    z.object({
        t: z.literal("delete"),
        depth: z.number().default(999),
    }),
]);

export const zSourceData = z.object({
    depth: z.number(),
    index: z.number().min(0),
    block: zCmsBlock,
    parentId: z.string().nullable(),
});

export const zCopyPayload = z.object({
    order: z.array(z.string()),
    blocks: z.array(zCmsBlock),
});

export type CopyPayload = z.infer<typeof zCopyPayload>;

// type ImmerAtom = WritableAtom<
//     Immutable<CmsBlock>,
//     [CmsBlock | ((draft: Draft<CmsBlock>) => void)],
//     void
// >;

type ImmerAtom = PrimitiveAtom<Immutable<CmsBlock>>;

export type LayoutEditDispatch = Dispatch<Action>;

export type LayoutEditState = {
    layout: { blocks: Record<string, ImmerAtom>; order: string[] };
    activeBlock: string | null;
};

export type Action =
    | {
          type: "move-block";
          sourceParentId: string | null;
          targetParentId: string | null;
          sourceIndex: number;
          newIndex: number;
      }
    | {
          type: "set-content";
          content: CmsLayout;
      }
    | {
          type: "add-block";
          index: number;
          parentId: string | null;
          block: CmsBlock;
      }
    | {
          type: "delete-block";
          blockId: string;
      }
    | {
          type: "set-active-block";
          blockId: string | null;
      }
    | {
          type: "duplicate-block";
          blockId: string;
      }
    | {
          type: "paste";
          payload: CopyPayload;
          index: number;
          parentId: string | null;
      };

export function initialState(): LayoutEditState {
    return { layout: { blocks: {}, order: [] }, activeBlock: null };
}

export function updateState(
    state: Immutable<LayoutEditState>,
    action: Action,
    store: ReturnType<typeof useStore>,
    onPatches: OnPatches
): Immutable<LayoutEditState> {
    const mutateOrder = (parentId: string | null, mutate: (draft: string[]) => void) => {
        if (parentId) {
            store.set(state.layout.blocks[parentId], prev => {
                return produce(
                    prev,
                    draft => {
                        invariant(
                            draft.kind.t === "Container" ||
                                draft.kind.t === "Row" ||
                                draft.kind.t === "Column"
                        );
                        mutate(draft.kind.c.blocks);
                    },
                    onPatches(parentId)
                );
            });
        } else {
            state = produce(
                state,
                draft => {
                    mutate(draft.layout.order);
                },
                onPatches(null)
            );
        }
    };

    // Runs the mutation for all blocks that can contain sub-blocks. Not the most efficient of
    // operations, but easy to reason about and more than fast enough
    const mutateOrderAllBlocks = (mutate: (draft: string[]) => void) => {
        mutateOrder(null, mutate);

        for (const blockId of Object.keys(state.layout.blocks)) {
            const block = store.get(state.layout.blocks[blockId]);
            if (block.kind.t === "Container" || block.kind.t === "Row") {
                mutateOrder(blockId, mutate);
            }
        }
    };

    switch (action.type) {
        case "move-block": {
            let removed: string | undefined;

            mutateOrder(action.sourceParentId, draft => {
                const [removed_] = draft.splice(action.sourceIndex, 1);
                removed = removed_;
            });

            if (removed) {
                const removedId = removed;
                mutateOrder(action.targetParentId, draft => {
                    draft.splice(action.newIndex, 0, removedId);
                });
            }

            break;
        }
        case "set-content": {
            const blocks: Record<string, ImmerAtom> = {};

            for (const blockId of Object.keys(action.content.blocks)) {
                blocks[blockId] = atom(castImmutable(action.content.blocks[blockId]!));
            }

            return {
                ...state,
                layout: {
                    order: action.content.order,
                    blocks,
                },
            };
        }
        case "add-block": {
            mutateOrder(action.parentId, draft => {
                draft.splice(action.index, 0, action.block.id);
            });

            return produce(
                state,
                draft => {
                    draft.layout.blocks[action.block.id] = atom(castImmutable(action.block));
                },
                onPatches(null)
            );
        }
        case "paste": {
            const blocks = action.payload.blocks;

            const [newBlocks, lookup] = createNewIds(blocks);

            mutateOrder(action.parentId, draft => {
                draft.splice(action.index, 0, ...action.payload.order.map(id => lookup[id]));
            });

            return produce(
                state,
                draft => {
                    for (const block of newBlocks) {
                        draft.layout.blocks[block.id] = atom(block);
                    }
                },
                onPatches(null)
            );
        }
        case "duplicate-block": {
            const payload = createCopyPayload(store, action.blockId, state);

            const [newBlocks, lookup] = createNewIds(payload.blocks);

            const index = findBlockIndex(state, action.blockId, store) ?? {
                index: 0,
                parentId: null,
            };

            mutateOrder(index.parentId, draft => {
                draft.splice(index.index + 1, 0, ...payload.order.map(id => lookup[id]));
            });

            return produce(
                state,
                draft => {
                    for (const block of newBlocks) {
                        draft.layout.blocks[block.id] = atom(block);
                    }
                },
                onPatches(null)
            );
        }
        case "delete-block": {
            // Blocks that we will have to check
            const blocksToDelete: string[] = [];
            // Blocks that we want to check if they have children
            const blocksToCheck = [action.blockId];

            for (;;) {
                const blockId = blocksToCheck.pop();
                if (!blockId) break;
                blocksToDelete.push(blockId);

                const blockAtom = state.layout.blocks[blockId];
                if (!blockAtom) {
                    continue;
                }

                const block = store.get(blockAtom);
                if (block.kind.t === "Container" || block.kind.t === "Row") {
                    for (const childBlockId of block.kind.c.blocks) {
                        if (!blocksToDelete.includes(childBlockId)) {
                            blocksToCheck.push(childBlockId);
                        }
                    }
                }
            }

            // Delete the reference to blocks everywhere they might be
            mutateOrderAllBlocks(draft => {
                const index = draft.findIndex(blockId => blockId === action.blockId);

                if (index != -1) {
                    draft.splice(index, 1);
                }
            });

            // Delete the actual blocks and set the active block to null it it was deleted
            return produce(
                state,
                draft => {
                    if (draft.activeBlock && blocksToDelete.includes(draft.activeBlock)) {
                        draft.activeBlock = null;
                    }

                    for (const deletedId of blocksToDelete) {
                        delete draft.layout.blocks[deletedId];
                    }
                },
                onPatches(null)
            );
        }
        case "set-active-block": {
            return produce(
                state,
                draft => {
                    draft.activeBlock =
                        draft.activeBlock === action.blockId ? null : action.blockId;
                },
                onPatches(null)
            );
        }
    }

    return state;
}

export type BlockIndex = {
    index: number;
    parentId: string | null;
};

export function findBlockIndex(
    state: Immutable<LayoutEditState>,
    blockId: string,
    store: ReturnType<typeof useStore>
): BlockIndex | null {
    const maybeIndex = state.layout.order.indexOf(blockId);
    if (maybeIndex !== -1) {
        return { index: maybeIndex, parentId: null };
    }

    for (const containerBlockId of Object.keys(state.layout.blocks)) {
        const block = store.get(state.layout.blocks[containerBlockId]);

        if (block.kind.t === "Container" || block.kind.t === "Row") {
            const maybeIndex = block.kind.c.blocks.indexOf(blockId);
            if (maybeIndex !== -1) {
                return { index: maybeIndex, parentId: block.id };
            }
        }
    }

    return null;
}

// **mutates** the input, making sure all ids are unique
export function createNewIds(
    blocks: Immutable<CmsBlock[]>
): [Immutable<CmsBlock[]>, Record<string, string>] {
    const lookup: Record<string, string> = {};
    const newBlocks = produce(blocks, draft => {
        for (const block of draft) {
            const newId = uuidv4();
            lookup[block.id] = newId;
            block.id = newId;
        }

        for (const block of draft) {
            if (block.kind.t === "Container" || block.kind.t === "Row") {
                block.kind.c.blocks = block.kind.c.blocks.map(id => lookup[id]);
            }

            if (block.kind.t === "Teaser") {
                block.kind.c.reactableId = uuidv4() as ReactableId;
            }
        }
    });

    return [newBlocks, lookup];
}

export function createCopyPayload(
    store: ReturnType<typeof useStore>,
    blockId: string,
    state: Immutable<LayoutEditState>
): Immutable<CopyPayload> {
    const blockContent = store.get(state.layout.blocks[blockId]);

    const blocks = [blockContent];

    if (blockContent.kind.t === "Container" || blockContent.kind.t === "Row") {
        blocks.push(...blockContent.kind.c.blocks.map(id => store.get(state.layout.blocks[id])));
    }

    return {
        blocks,
        order: [blockId],
    };
}
